import { useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useActiveStoreId } from "./campaigns/useActiveStoreId";
import type { ScheduledCoupon } from "@/types/scheduled-coupons";

export interface CouponMetricsData {
  totalRevenue: number;
  totalCoupons: number;
  couponsUsed: number;
  couponsActive: number;
  couponsPending: number;
  totalCouponValue: number;
  revenueByDay: Array<{ date: string; value: number }>;
  couponsByStatus: Array<{ status: string; count: number }>;
  couponsByDay: Array<{ date: string; count: number }>;
}

export const useCouponDashboardApi = () => {
  const { data: storeId } = useActiveStoreId();

  const fetchCouponMetrics = async (fromDate: string, toDate: string): Promise<CouponMetricsData> => {
    console.log('📊 fetchCouponMetrics called with:', { fromDate, toDate, storeId });
    
    if (!storeId) {
      console.warn('❌ No storeId found, returning empty metrics');
      return {
        totalRevenue: 0,
        totalCoupons: 0,
        couponsUsed: 0,
        couponsActive: 0,
        couponsPending: 0,
        totalCouponValue: 0,
        revenueByDay: [],
        couponsByStatus: [],
        couponsByDay: []
      };
    }

    // Fetch all coupons within date range
    const { data: coupons, error } = await supabase
      .from('campaign_generated_coupons')
      .select('*')
      .eq('store_id', storeId)
      .gte('created_at', fromDate + ' 00:00:00')
      .lte('created_at', toDate + ' 23:59:59')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching coupon metrics:', error);
      throw error;
    }

    const couponData = coupons as ScheduledCoupon[];

    // Calculate metrics
    const totalRevenue = couponData.reduce((sum, coupon) => sum + (coupon.total_spent_amount || 0), 0);
    const totalCoupons = couponData.length;
    const couponsUsed = couponData.filter(c => c.status === 'used').length;
    const couponsActive = couponData.filter(c => c.status === 'active').length;
    const couponsPending = couponData.filter(c => c.status === 'pending').length;
    const totalCouponValue = couponData.reduce((sum, coupon) => sum + (coupon.coupon_value || 0), 0);

    // Group revenue by day
    const revenueByDay = couponData.reduce((acc, coupon) => {
      const date = new Date(coupon.created_at).toISOString().split('T')[0];
      const existing = acc.find(item => item.date === date);
      if (existing) {
        existing.value += coupon.total_spent_amount || 0;
      } else {
        acc.push({ date, value: coupon.total_spent_amount || 0 });
      }
      return acc;
    }, [] as Array<{ date: string; value: number }>);

    // Group coupons by status
    const statusGroups = couponData.reduce((acc, coupon) => {
      acc[coupon.status] = (acc[coupon.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const couponsByStatus = Object.entries(statusGroups).map(([status, count]) => ({
      status,
      count
    }));

    // Group coupons by day
    const couponsByDay = couponData.reduce((acc, coupon) => {
      const date = new Date(coupon.created_at).toISOString().split('T')[0];
      const existing = acc.find(item => item.date === date);
      if (existing) {
        existing.count += 1;
      } else {
        acc.push({ date, count: 1 });
      }
      return acc;
    }, [] as Array<{ date: string; count: number }>);

    return {
      totalRevenue,
      totalCoupons,
      couponsUsed,
      couponsActive,
      couponsPending,
      totalCouponValue,
      revenueByDay: revenueByDay.sort((a, b) => a.date.localeCompare(b.date)),
      couponsByStatus,
      couponsByDay: couponsByDay.sort((a, b) => a.date.localeCompare(b.date))
    };
  };

  return useMemo(() => ({
    fetchCouponMetrics
  }), [storeId]); // Dependência do storeId para permitir re-execução quando mudare
};